import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WAKE_WORDS = [
  'hey sherpa', 'hey shilpa', 'hey shelpa',
  'hey sherbert', 'hey shirpa', 'hey sherpah',
  'a sherpa', 'asian', 'hey share', 'sherpa'
];

export default function Sherpa() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('sherpa_open') === 'true'; } catch { return false; }
  });
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'sherpa', text: 'Hey! I\'m Sherpa. Say "Hey Sherpa" or type below.' }
  ]);
  const [input, setInput] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isListeningForWake, setIsListeningForWake] = useState(false);

  const historyRef = useRef([]);
  const listeningRef = useRef(false);
  const commandModeRef = useRef(false);
  const openRef = useRef(false);
  const activeWakeRecRef = useRef(null);
  const activeCommandRecRef = useRef(null);
  const wakeRunningRef = useRef(false);
  // Refs-to-callbacks so the two session functions can reference each other
  // without a useCallback dependency cycle. Synced by the useEffect below.
  const startWakeSessionRef = useRef(() => {});
  const startCommandSessionRef = useRef(() => {});

  const navigate = useNavigate();

  useEffect(() => { listeningRef.current = listening; }, [listening]);
  useEffect(() => {
    openRef.current = open;
    try { localStorage.setItem('sherpa_open', open ? 'true' : 'false'); } catch {}
  }, [open]);

  // ── CONTEXT BUILDER ───────────────────────────────────────────────────
  const buildContext = useCallback(() => {
    const path = window.location.pathname;
    const screen = path.replace(/^\//, '') || 'home';
    let role = null;
    let demoData = null;
    try { role = JSON.parse(localStorage.getItem('reai_role') || 'null'); } catch {}
    try { demoData = JSON.parse(localStorage.getItem('demo_signal_data') || 'null'); } catch {}

    const slug = localStorage.getItem('demo_slug') ||
      window.location.pathname.split('/demo/')?.[1]?.split('/')?.[0] ||
      null;

    return {
      currentScreen: screen,
      org: localStorage.getItem('demo_org_name') || demoData?.org_name || 'Unknown org',
      sector: localStorage.getItem('demo_sector') || demoData?.sector_display || '',
      gapFigure: demoData?.asbc_gap || demoData?.gap_figure || 'Unknown',
      currency: demoData?.currency || 'USD',
      signal: demoData?.signal_band || demoData?.signal || 'Unknown',
      currentRole: role?.name || null,
      activities: role?.activities?.length || 0,
      subs: role?.activities?.reduce((n, a) => n + (a.subs?.length || 0), 0) || 0,
      userRole: localStorage.getItem('role') || 'unknown',
      isDemo: localStorage.getItem('is_demo') === 'true',
      demoSlug: slug,
      availableScreens: [
        '/signal-room', '/intelligence', '/blueprint/cockpit',
        '/deconstruct', '/analyze', '/sort', '/recompose', '/analysis'
      ]
    };
  }, []);

  // ── SYSTEM PROMPT ─────────────────────────────────────────────────────
  const buildSystemPrompt = (ctx) => `You are reAI Sherpa, the AI guide inside reAImagination — the Work AIrchitecture platform.

CURRENT CONTEXT:
Screen: ${ctx.currentScreen}
Organisation: ${ctx.org}
Sector: ${ctx.sector}
Transformation gap: ${ctx.gapFigure} (${ctx.currency})
Signal: ${ctx.signal}
Demo slug: ${ctx.demoSlug || 'direct login'}
Current role: ${ctx.currentRole || 'None loaded'}
Activities: ${ctx.activities} · Sub-tasks: ${ctx.subs}

AVAILABLE SCREENS you can navigate to:
/signal-room — Signal Room (gap overview)
/intelligence — Org Intelligence (treemap)
/blueprint/cockpit — Blueprint Cockpit
/deconstruct — Deconstruct (activity rails)
/analyze — Analyze (opportunity spine)
/sort — Sort 4B (rail matrix)
/recompose — Recompose (convergence)
/analysis — Analysis (upload roles)
/analysis/discipline — Value Discipline
/analysis/results — Transformation Results
/showcase — Showcase

YOUR CAPABILITIES:
Navigate, explain current screen, summarise data,
execute workflow actions, filter by 4B bucket.

4B BUCKETS: bot, borrow, build, bi, buy

RESPONSE — always return valid JSON only, no markdown:
{
  "response": "max 20 words, conversational, warm",
  "action": {
    "type": "navigate|filter|execute|highlight|none",
    "to": "/path",
    "bucket": "bot|borrow|build|bi|buy",
    "target": "generate-blueprint|proceed|reset"
  } | null
}

RULES:
- Max 20 words in response — it will be spoken aloud
- If unclear, ask ONE short clarifying question
- Never invent data not in context
- Always confirm executed actions
- Be warm, efficient, expert`;

  // ── ACTION EXECUTOR ───────────────────────────────────────────────────
  const executeAction = useCallback((action) => {
    console.log('Sherpa action:', JSON.stringify(action));
    if (!action || !action.type || action.type === 'none') return;
    switch (action.type) {
      case 'navigate':
        if (action.to) {
          console.log('Navigating to:', action.to);
          navigate(action.to);
          // Fallback in case navigate() doesn't actually change the URL
          setTimeout(() => {
            if (window.location.pathname !== action.to) {
              window.location.href = action.to;
            }
          }, 500);
        }
        break;
      case 'filter':
        window.dispatchEvent(new CustomEvent('sherpa-filter', { detail: { bucket: action.bucket } }));
        break;
      case 'execute':
        window.dispatchEvent(new CustomEvent('sherpa-execute', { detail: { target: action.target } }));
        break;
      case 'highlight':
        window.dispatchEvent(new CustomEvent('sherpa-highlight', { detail: { entity: action.entity } }));
        break;
      default:
        break;
    }
  }, [navigate]);

  // ── VOICE OUTPUT ──────────────────────────────────────────────────────
  const speak = useCallback((text) => {
    if (!window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 1.05;
    utt.pitch = 1.0;
    utt.volume = 0.9;
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(v =>
        v.name.includes('Samantha') ||
        v.name.includes('Karen') ||
        v.name.includes('Google UK English Female')
      );
      if (preferred) utt.voice = preferred;
      window.speechSynthesis.speak(utt);
    };
    if (window.speechSynthesis.getVoices().length) {
      loadVoices();
    } else {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // ── SEND TO SHERPA (via backend proxy) ────────────────────────────────
  const sendToSherpa = useCallback(async (userText) => {
    if (!userText.trim()) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setMessages(prev => [...prev, { role: 'sherpa', text: 'Please sign in first.' }]);
      return;
    }

    const userMsg = { role: 'user', text: userText };
    setMessages(prev => [...prev, userMsg]);
    historyRef.current = [...historyRef.current, { role: 'user', content: userText }];
    setThinking(true);
    setInput('');
    setLiveTranscript('');

    try {
      const ctx = buildContext();
      const res = await fetch(`${BACKEND_URL}/api/chro/sherpa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          system: buildSystemPrompt(ctx),
          messages: historyRef.current,
          max_tokens: 300
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const raw = data.content || '{}';

      let parsed;
      try {
        const clean = raw.replace(/```json|```/g, '').trim();
        parsed = JSON.parse(clean);
      } catch {
        parsed = { response: raw, action: null };
      }

      const sherpaMsg = { role: 'sherpa', text: parsed.response };
      setMessages(prev => [...prev, sherpaMsg]);
      historyRef.current = [...historyRef.current, { role: 'assistant', content: parsed.response }];

      if (parsed.action) executeAction(parsed.action);
      speak(parsed.response);
    } catch (err) {
      const errMsg = { role: 'sherpa', text: 'Sorry, I had trouble connecting. Try again.' };
      setMessages(prev => [...prev, errMsg]);
      speak(errMsg.text);
    } finally {
      setThinking(false);
    }
  }, [buildContext, executeAction, speak]);

  // ── WAKE SESSION (polling, single-utterance) ─────────────────────────
  const startWakeSession = useCallback(async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || wakeRunningRef.current || commandModeRef.current) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      console.warn('Sherpa: mic not available', err);
      return;
    }

    const rec = new SR();
    activeWakeRecRef.current = rec;
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      wakeRunningRef.current = true;
      setIsListeningForWake(true);
    };

    rec.onresult = (e) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      transcript = transcript.toLowerCase().trim();
      console.log('Wake heard:', transcript);
      const wakeHit = WAKE_WORDS.some(w => transcript.includes(w));
      console.log('Wake hit:', wakeHit);
      if (!wakeHit) return;

      let cmd = transcript;
      WAKE_WORDS.forEach(w => { cmd = cmd.split(w).join(' '); });
      cmd = cmd.replace(/\s+/g, ' ').trim();
      console.log('Command extracted:', cmd);

      commandModeRef.current = true;
      try { rec.stop(); } catch {}

      if (cmd.length > 2) {
        setOpen(true);
        openRef.current = true;
        console.log('Calling sendToSherpa with:', cmd);
        sendToSherpa(cmd);
        commandModeRef.current = false;
        // Panel open after direct send → let the open-triggered useEffect
        // start a command session for the next utterance.
      } else {
        setOpen(true);
        openRef.current = true;
        setListening(true);
        startCommandSessionRef.current();
      }
    };

    rec.onerror = (e) => {
      wakeRunningRef.current = false;
      activeWakeRecRef.current = null;
      setIsListeningForWake(false);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') return;
      if (!commandModeRef.current) setTimeout(() => startWakeSessionRef.current(), 2000);
    };

    rec.onend = () => {
      console.log('Wake onend, commandMode:', commandModeRef.current);
      wakeRunningRef.current = false;
      activeWakeRecRef.current = null;
      setIsListeningForWake(false);
      if (!commandModeRef.current) setTimeout(() => startWakeSessionRef.current(), 1000);
    };

    try {
      rec.start();
      console.log('Sherpa wake session started');
    } catch {}
  }, [sendToSherpa]);

  // ── COMMAND SESSION (continuous + silence auto-send) ─────────────────
  const startCommandSession = useCallback(async () => {
    console.log('startCommandSession called, commandMode:', commandModeRef.current);
    if (commandModeRef.current) return;
    commandModeRef.current = true;

    // If a wake-session recognizer is still running, stop it so the mic
    // is released before we try to start the command recognizer.
    if (activeWakeRecRef.current) {
      try { activeWakeRecRef.current.stop(); } catch {}
      activeWakeRecRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch (err) {
      console.warn('Sherpa: mic not available', err);
      setListening(false);
      commandModeRef.current = false;
      setTimeout(() => startWakeSessionRef.current(), 500);
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { commandModeRef.current = false; return; }

    const rec = new SR();
    activeCommandRecRef.current = rec;
    rec.continuous = true;       // Chrome otherwise closes after ~1s of silence
    rec.interimResults = true;
    rec.lang = 'en-US';

    let accumulated = '';
    let silenceTimer = null;

    const finish = (reason) => {
      clearTimeout(silenceTimer);
      const toSend = accumulated.trim();
      accumulated = '';
      setListening(false);
      setLiveTranscript('');
      activeCommandRecRef.current = null;
      commandModeRef.current = false;

      if (toSend) {
        console.log(`Sending on ${reason}:`, toSend);
        sendToSherpa(toSend);
      }
      // Hands-free continuation: panel open → loop command mode; closed → wake.
      if (openRef.current) {
        console.log('Panel open after command — looping command mode');
        setTimeout(() => startCommandSessionRef.current(), 400);
      } else {
        console.log('Panel closed — resuming wake session');
        setTimeout(() => startWakeSessionRef.current(), 500);
      }
    };

    rec.onresult = (e) => {
      let final = '';
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }

      if (final) accumulated += ' ' + final;
      const display = accumulated.trim() || interim.trim();
      console.log('Command heard:', final, 'interim:', interim, 'accumulated:', accumulated.trim());
      setLiveTranscript(display);

      clearTimeout(silenceTimer);
      const pending = accumulated.trim() || interim.trim();
      if (pending.length > 1) {
        silenceTimer = setTimeout(() => {
          // Lift interim into accumulated if nothing was finalized yet,
          // then stop the recognizer — rec.onend → finish() does the send.
          if (!accumulated.trim()) accumulated = pending;
          try { rec.stop(); } catch {}
        }, 2000);
      }
    };

    rec.onerror = () => finish('error');
    rec.onend = () => finish('end');

    try {
      rec.start();
      setListening(true);
      console.log('Sherpa command session started');
    } catch {
      commandModeRef.current = false;
    }
  }, [sendToSherpa]);

  // Sync ref-to-callback on every render so each session fn can call the
  // other via startXxxSessionRef.current() without dep-cycling the hooks.
  useEffect(() => {
    startWakeSessionRef.current = startWakeSession;
    startCommandSessionRef.current = startCommandSession;
  });

  // ── GESTURE-DEFERRED INITIAL WAKE START ───────────────────────────────
  useEffect(() => {
    const startOnGesture = () => {
      startWakeSessionRef.current();
      document.removeEventListener('click', startOnGesture);
      document.removeEventListener('keydown', startOnGesture);
    };
    document.addEventListener('click', startOnGesture);
    document.addEventListener('keydown', startOnGesture);
    return () => {
      document.removeEventListener('click', startOnGesture);
      document.removeEventListener('keydown', startOnGesture);
    };
  }, []);

  // ── OPEN-TRIGGERED AUTO-RESTART ───────────────────────────────────────
  // Whenever the panel is open and no command session is running, start one.
  // Covers the case where finish()'s setTimeout didn't fire (React cadence,
  // React re-render, closure staleness) — this useEffect uses the live
  // React `open` state so it always runs after the last state commit.
  useEffect(() => {
    if (!open) return;
    if (commandModeRef.current) return;
    const t = setTimeout(() => {
      if (openRef.current && !commandModeRef.current) {
        startCommandSession();
      }
    }, 400);
    return () => clearTimeout(t);
  }, [open, startCommandSession]);

  // ── ACTIVATION GATE ───────────────────────────────────────────────────
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isActive = !!(
    (typeof localStorage !== 'undefined' && (localStorage.getItem('token') || localStorage.getItem('is_demo') === 'true')) ||
    path.includes('/demo/') ||
    path.includes('/signal-room') ||
    path.includes('/intelligence') ||
    path.includes('/blueprint') ||
    path.includes('/deconstruct') ||
    path.includes('/analyze') ||
    path.includes('/sort') ||
    path.includes('/recompose') ||
    path.includes('/analysis')
  );

  if (!isActive) return null;

  const hasSR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stopActiveCommand = () => {
    if (activeCommandRecRef.current) {
      try { activeCommandRecRef.current.stop(); } catch {}
      activeCommandRecRef.current = null;
    }
  };
  const stopActiveWake = () => {
    if (activeWakeRecRef.current) {
      try { activeWakeRecRef.current.stop(); } catch {}
      activeWakeRecRef.current = null;
    }
  };

  return (
    <>
      <div
        className="sherpa-bubble"
        onClick={() => {
          const newOpen = !open;
          setOpen(newOpen);
          openRef.current = newOpen;
          if (newOpen) {
            // Stop wake rec; the open-triggered useEffect will start command.
            stopActiveWake();
            setListening(true);
          } else {
            // Closing — tear down command, resume wake.
            commandModeRef.current = false;
            stopActiveCommand();
            setTimeout(() => startWakeSessionRef.current(), 500);
          }
        }}
        title={isListeningForWake ? "Say 'Hey Sherpa' to activate" : 'Open Sherpa'}
        style={{
          animation: listening ? 'none' : undefined,
          boxShadow: listening
            ? '0 0 0 14px rgba(0,212,200,0.25), 0 4px 28px rgba(0,212,200,0.6)'
            : isListeningForWake
              ? '0 0 0 6px rgba(0,212,200,0.12), 0 4px 20px rgba(0,212,200,0.35)'
              : undefined,
          background: listening ? '#00D4C8' : 'var(--teal)'
        }}
      >
        {listening ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0A0A1A" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        ) : thinking ? (
          <div style={{ display: 'flex', gap: 3 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 5, height: 5, borderRadius: '50%',
                background: '#0A0A1A',
                animation: `sherpa-dot 1.2s ease-in-out ${i * 0.2}s infinite`
              }} />
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 20, color: '#0A0A1A', fontFamily: 'var(--fd)', fontWeight: 700 }}>◈</span>
        )}
      </div>

      {open && (
        <div className="sherpa-panel glass-strong">
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--line)',
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--teal)', fontSize: 16 }}>◈</span>
              <span style={{ fontFamily: 'var(--fd)', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
                re<span style={{ color: 'var(--teal)' }}>AI</span> Sherpa
              </span>
            </div>
            <button onClick={() => {
              setOpen(false);
              openRef.current = false;
              commandModeRef.current = false;
              stopActiveCommand();
              setTimeout(() => startWakeSessionRef.current(), 500);
            }} style={{
              background: 'none', border: 'none', color: 'var(--t4)',
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 6px'
            }}>×</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 16px',
            display: 'flex', flexDirection: 'column', gap: 10
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '80%', padding: '8px 12px',
                  borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: m.role === 'user' ? 'var(--teal)' : 'rgba(255,255,255,0.06)',
                  color: m.role === 'user' ? 'var(--bg)' : 'var(--t2)',
                  fontSize: 13, fontFamily: 'var(--fs)', lineHeight: 1.5,
                  border: m.role === 'sherpa' ? '1px solid var(--line)' : 'none'
                }}>
                  {m.text}
                </div>
              </div>
            ))}
            {listening && !liveTranscript && (
              <div style={{
                fontSize: 11, color: 'var(--teal)',
                fontFamily: 'var(--fm)', fontStyle: 'italic',
                textAlign: 'right', letterSpacing: '0.04em'
              }}>
                Listening…
              </div>
            )}
            {liveTranscript && (
              <div style={{
                fontSize: 11, color: 'var(--t4)',
                fontFamily: 'var(--fm)', fontStyle: 'italic',
                textAlign: 'right'
              }}>
                "{liveTranscript}"
              </div>
            )}
            {thinking && (
              <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: 'var(--teal)',
                    animation: `sherpa-dot 1.2s ease-in-out ${i * 0.2}s infinite`
                  }} />
                ))}
              </div>
            )}
          </div>

          {/* Input row */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid var(--line)',
            display: 'flex', gap: 8, alignItems: 'center',
            flexShrink: 0
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && input.trim()) sendToSherpa(input);
              }}
              placeholder={
                !hasSR
                  ? 'Ask Sherpa anything...'
                  : isListeningForWake
                    ? 'Listening for "Hey Sherpa"...'
                    : 'Ask Sherpa or type here...'
              }
              style={{
                flex: 1, background: 'rgba(255,255,255,0.06)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
                padding: '8px 12px', color: 'var(--text)',
                fontFamily: 'var(--fs)', fontSize: 13,
                outline: 'none'
              }}
            />
            {hasSR && (
              <button
                onClick={async () => {
                  stopActiveWake();
                  stopActiveCommand();
                  commandModeRef.current = false;
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach(t => t.stop());
                    setListening(true);
                    setLiveTranscript('...');
                    startCommandSession();
                  } catch (err) {
                    console.warn('Mic denied:', err);
                  }
                }}
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: listening ? 'var(--teal)' : 'rgba(0,212,200,0.12)',
                  border: '1px solid rgba(0,212,200,0.3)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                </svg>
              </button>
            )}
            <button
              onClick={() => sendToSherpa(input)}
              disabled={!input.trim() && !thinking}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'var(--teal)', border: 'none',
                cursor: 'pointer', color: 'var(--bg)',
                fontSize: 16, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                opacity: input.trim() ? 1 : 0.4
              }}
            >→</button>
          </div>
        </div>
      )}
    </>
  );
}
